import React, { useState, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  InputGroup,
  InputLeftElement,
  Textarea,
  VStack,
  useToast,
  Switch,
  Text,
  HStack,
  Flex,
  Image,
} from "@chakra-ui/react";
import { debounce } from "lodash";
import { FaDollarSign } from "react-icons/fa";
import { FiLink } from "react-icons/fi";
import { secureFetch } from "../data/secureFetch";
import { BANNER_IMAGE_RECOMMENDATION } from "../utils/bannerImage";
import useBannerImageUpload from "../hooks/useBannerImageUpload";
const { formatDollarsToCents } = require("../store/calc-helpers");

const CreateCampaign = () => {
  const navigate = useNavigate();
  const { organizationId } = useSelector((state) => state.user);
  const [formData, setFormData] = useState({
    name: "",
    goal: "",
    startDate: "",
    endDate: "",
    url: "",
    description: "",
    status: "active",
  });

  const [showGoal, setShowGoal] = useState(false);
  const [showDates, setShowDates] = useState(false);
  const [cleanedUrl, setCleanedUrl] = useState("");
  const [urlAvailable, setUrlAvailable] = useState(true);
  const [manualUrlEdit, setManualUrlEdit] = useState(false);
  const toast = useToast();
  const {
    bannerImageDataUrl,
    bannerImagePreview,
    bannerImageFileName,
    bannerImageWarning,
    handleBannerImageChange,
    resetBannerImage,
  } = useBannerImageUpload();

  const checkUrlAvailability = useMemo(
    () =>
      debounce(async (url) => {
        if (!url) {
          setUrlAvailable(false);
          return;
        }
        const response = await secureFetch(`/api/campaigns/check-url`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          data: { url },
        });
        const { available } = response;
        setUrlAvailable(available);
      }, 500),
    [],
  );

  useEffect(
    () => () => {
      checkUrlAvailability.cancel();
    },
    [checkUrlAvailability],
  );

  useEffect(() => {
    if (!manualUrlEdit || formData.url === "") {
      const formattedUrl = formData.name
        .trim()
        .replace(/^[^a-zA-Z0-9]+/, "") // Remove leading non-alphanumeric characters
        .replace(/[^a-zA-Z0-9]+/g, "_") // Replace other non-alphanumeric characters with underscores
        .replace(/_+$/, "") // Remove trailing underscores
        .toLowerCase();
      if (formattedUrl !== formData.url) {
        setFormData((prev) => ({ ...prev, url: formattedUrl }));
      }
      setCleanedUrl(formattedUrl);
      checkUrlAvailability(formattedUrl);
    }
  }, [formData.name, formData.url, manualUrlEdit, checkUrlAvailability]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "goal" && !/^\d*\.?\d{0,2}$/.test(value)) {
      return; // Ensure only valid numbers with up to 2 decimal places are allowed
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (name === "url") {
      setManualUrlEdit(true);
      const formattedUrl = value
        .trim()
        .replace(/^[^a-zA-Z0-9]+/, "") // Remove leading non-alphanumeric characters
        .replace(/[^a-zA-Z0-9]+/g, "_") // Replace other non-alphanumeric characters with underscores
        .replace(/_+$/, "") // Remove trailing underscores
        .toLowerCase();
      setCleanedUrl(formattedUrl);
      checkUrlAvailability(formattedUrl);
    }
  };

  const generateRandomUrl = () => {
    const randomId = Math.random().toString(36).substr(2, 10);
    setFormData((prev) => ({ ...prev, url: randomId }));
    setCleanedUrl(randomId);
    setManualUrlEdit(true);
    checkUrlAvailability(randomId);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate goal and dates
    const goal = showGoal ? formatDollarsToCents(formData.goal) : 0;
    const startDate = showDates ? new Date(formData.startDate) : null;
    const endDate = showDates ? new Date(formData.endDate) : null;

    if (showGoal && isNaN(goal)) {
      toast({
        title: "Invalid Goal",
        description: "The goal amount must be an integer.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    if (showDates && (isNaN(startDate) || isNaN(endDate))) {
      toast({
        title: "Invalid Dates",
        description: "Please provide valid start and end dates.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    if (!urlAvailable) {
      toast({
        title: "URL Unavailable",
        description:
          "The provided URL is already in use or invalid. Please choose a different URL.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    const campaignData = {
      name: formData.name,
      goal,
      description: formData.description,
      url: cleanedUrl,
      status: formData.status,
      organizationId,
    };

    if (showDates) {
      campaignData.startDate = startDate;
      campaignData.endDate = endDate;
    }

    try {
      const response = await secureFetch(`/api/campaigns`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        data: campaignData,
      });

      if (response.ok) {
        let bannerUploadWarning = "";
        if (bannerImageDataUrl && response.id) {
          const bannerResponse = await secureFetch(
            `/api/campaigns/${response.id}/banner`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              data: { imageDataUrl: bannerImageDataUrl },
            },
          );
          if (!bannerResponse.ok) {
            bannerUploadWarning =
              bannerResponse.message ||
              "Campaign created, but banner upload failed.";
          }
        }

        toast({
          title: "Campaign Created",
          description:
            bannerUploadWarning ||
            "The campaign has been created successfully!",
          status: bannerUploadWarning ? "warning" : "success",
          duration: 5000,
          isClosable: true,
        });
        setFormData({
          name: "",
          goal: "",
          startDate: "",
          endDate: "",
          url: "",
          description: "",
          status: "active",
        });
        setShowGoal(false);
        setShowDates(false);
        setCleanedUrl("");
        setManualUrlEdit(false);
        resetBannerImage();

        // Redirect to campaigns route
        navigate("/campaigns");
      } else {
        throw new Error(response.message || "Failed to create campaign");
      }
    } catch (error) {
      toast({
        title: "Campaign Creation Failed",
        description: error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  return (
    <Box p={5}>
      <VStack as="form" onSubmit={handleSubmit} spacing={4}>
        <FormControl isRequired>
          <FormLabel htmlFor="name">Campaign Name</FormLabel>
          <Input
            id="name"
            name="name"
            type="text"
            value={formData.name}
            onChange={handleChange}
            required
          />
        </FormControl>

        <FormControl display="flex" alignItems="center">
          <FormLabel htmlFor="goalToggle" mb="0">
            Set Target Amount
          </FormLabel>
          <Switch
            id="goalToggle"
            isChecked={showGoal}
            onChange={() => {
              setShowGoal((prev) => !prev);
              setFormData((prev) => ({ ...prev, goal: "" }));
            }}
          />
        </FormControl>
        {showGoal && (
          <FormControl isRequired>
            <FormLabel htmlFor="goal">Goal</FormLabel>
            <InputGroup>
              <InputLeftElement
                pointerEvents="none"
                color="gray.500"
                fontSize="1.4em"
              >
                <FaDollarSign />
              </InputLeftElement>
              <Input
                id="goal"
                name="goal"
                type="number"
                value={formData.goal}
                onChange={handleChange}
                required
              />
            </InputGroup>
          </FormControl>
        )}

        <FormControl display="flex" alignItems="center">
          <FormLabel htmlFor="datesToggle" mb="0">
            Set Start and End Dates
          </FormLabel>
          <Switch
            id="datesToggle"
            isChecked={showDates}
            onChange={() => setShowDates((prev) => !prev)}
          />
        </FormControl>
        {showDates && (
          <HStack spacing={4} width="full">
            <FormControl isRequired>
              <FormLabel htmlFor="startDate">Start Date</FormLabel>
              <Input
                id="startDate"
                name="startDate"
                type="date"
                value={formData.startDate}
                onChange={handleChange}
                required
              />
            </FormControl>
            <FormControl isRequired>
              <FormLabel htmlFor="endDate">End Date</FormLabel>
              <Input
                id="endDate"
                name="endDate"
                type="date"
                value={formData.endDate}
                onChange={handleChange}
                required
              />
            </FormControl>
          </HStack>
        )}

        <FormControl isRequired>
          <FormLabel htmlFor="url">URL</FormLabel>
          <HStack>
            <InputGroup>
              <InputLeftElement
                pointerEvents="none"
                color="gray.500"
                fontSize="1.4em"
              >
                <FiLink />
              </InputLeftElement>
              <Input
                id="url"
                name="url"
                type="text"
                value={formData.url}
                onChange={handleChange}
                required
              />
            </InputGroup>
            <Button onClick={generateRandomUrl}>Generate</Button>
          </HStack>
          <Flex
            mt={1}
            direction={{ base: "column", sm: "row" }} // Stack vertically on small screens
            align="flex-start"
            flexWrap="wrap"
          >
            <Text
              fontSize="sm"
              color="gray.500"
              wordBreak="break-all" // Allow long words (like URLs) to break and wrap
            >
              Preview: https://purelygive.com/donate/{cleanedUrl}
            </Text>
            <Text
              ml={{ base: 0, sm: 2 }} // Adjust margin based on screen size
              mt={{ base: 1, sm: 0 }}
              fontSize="sm"
              color={urlAvailable ? "green.500" : "red.500"}
            >
              {urlAvailable ? "URL is available" : "URL is not available"}
            </Text>
          </Flex>
        </FormControl>

        <FormControl>
          <FormLabel htmlFor="description">Description</FormLabel>
          <Textarea
            id="description"
            name="description"
            value={formData.description}
            onChange={handleChange}
          />
        </FormControl>

        <FormControl>
          <FormLabel htmlFor="bannerImage">Banner Image (Optional)</FormLabel>
          <Input
            id="bannerImage"
            name="bannerImage"
            type="file"
            accept="image/png,image/jpeg,image/webp"
            onChange={handleBannerImageChange}
            p={1}
          />
          <Text fontSize="sm" color="gray.600" mt={1}>
            Recommended: {BANNER_IMAGE_RECOMMENDATION.recommendedWidth}x
            {BANNER_IMAGE_RECOMMENDATION.recommendedHeight}px. Minimum:{" "}
            {BANNER_IMAGE_RECOMMENDATION.minimumWidth}x
            {BANNER_IMAGE_RECOMMENDATION.minimumHeight}px. Max{" "}
            {BANNER_IMAGE_RECOMMENDATION.maxFileSizeMB}MB.
          </Text>
          {bannerImageFileName && (
            <Text fontSize="sm" color="gray.700" mt={1}>
              Selected: {bannerImageFileName}
            </Text>
          )}
          {bannerImageWarning && (
            <Text fontSize="sm" color="orange.500" mt={1}>
              {bannerImageWarning}
            </Text>
          )}
          {bannerImagePreview && (
            <Box mt={3} borderWidth="1px" borderRadius="md" overflow="hidden">
              <Image
                src={bannerImagePreview}
                alt="Banner preview"
                width="100%"
                maxH="160px"
                objectFit="cover"
              />
            </Box>
          )}
        </FormControl>

        <Button mt={4} colorScheme="orange" type="submit">
          Create Campaign
        </Button>
      </VStack>
    </Box>
  );
};

export default CreateCampaign;
